import { Alert } from 'src/design/atoms/Alert';
import { Box } from 'src/design/atoms/Box';
import { Card } from 'src/design/atoms/Card';
import { Grid } from 'src/design/atoms/Grid';
import { Group } from 'src/design/atoms/Group';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Checkbox } from 'src/design/atoms/Checkbox';
import { Input } from 'src/design/atoms/Input';
import { Select } from 'src/design/atoms/Select';
import { Tabs } from 'src/design/molecules/Tabs';
import { TextInput } from 'src/design/atoms/TextInput';
import { IconUserSearch } from '@tabler/icons-react';
import { useFormik } from 'formik';
import { uniq } from 'lodash';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DivisionDto,
  InviteUserRequest,
  OrganizationOfficeDto,
  OrganizationRoleDto,
  UpdateUserRequest,
  UserDto,
} from 'src/api/FiretrolApiClient';
import {
  useInviteUser,
  useUpdateUser,
  useUpdateUserStatus,
} from 'src/api/queries/organization/useUserMutations';
import { useAdminUsers } from 'src/api/queries/organization/useAdminUsers';
import { DivisionList } from 'src/app/organization/DivisionList';
import { OfficeSelector } from 'src/app/selectors/OfficeSelectorMulti';
import { RoleSelector2 } from 'src/routes/Settings/Users/components/UserForm/RoleSelector2';
import { UserSelect } from 'src/app/selectors/UserSelect';
import { useAuth } from 'src/hooks/useAuth';
import { CreateButton } from 'src/design/atoms/CreateButton';
import { InlineConfirmButton } from 'src/design/molecules/InlineConfirmButton';
import { useConfirmableAction } from 'src/design/molecules/useConfirmableAction';
import { ModalButton } from 'src/design/molecules/ModalButton';
import { SaveButton } from 'src/design/atoms/SaveButton';
import { SecondaryButton } from 'src/design/atoms/SecondaryButton';
import { TagBadgeList } from 'src/design/molecules/TagBadgeList';
import {
  applyValidationErrors,
  getErrorMessage,
  showErrorToast,
  showSuccessToast,
} from 'src/utils';
import { formatDate } from 'src/utils/format';
import * as Yup from 'yup';
import { ArchiveUserButton } from './ArchiveUserButton';
import { JobGrantSelector } from './JobGrantSelector';
import { NotificationChannelStatus } from './NotificationChannelStatus';
import { OfficeDivisionSelector } from './OfficeDivisionSelector';
import { RestoreArchivedUserModal } from './RestoreArchivedUserModal';
import { UserLogins } from './UserLogins';

// Extend the request DTO with full DTOs needed for form selectors
interface UserFormValues
  extends Omit<
    InviteUserRequest,
    'roleIds' | 'officeIds' | 'divisionIds' | 'primaryOfficeId'
  > {
  id?: number;
  roles: OrganizationRoleDto[];
  offices: OrganizationOfficeDto[];
  divisions: DivisionDto[];
  primaryOffice: OrganizationOfficeDto | null;
  allowedJobIds: number[];
  allowedCtcJobNumbers: string[];
}

// Convert form values to API request format
const toInviteRequest = (values: UserFormValues): InviteUserRequest => ({
  ...values,
  email: values.email.trim(),
  roleIds: values.roles.map((r) => r.id),
  officeIds: values.offices.map((o) => o.id),
  divisionIds: values.divisions.map((d) => d.id),
  primaryOfficeId: values.primaryOffice?.id ?? undefined,
});

const toUpdateRequest = (values: UserFormValues): UpdateUserRequest => ({
  ...toInviteRequest(values),
});

const createValidationSchema = () =>
  Yup.object().shape({
    firstName: Yup.string()
      .required()
      .min(1, 'First Name Required')
      .label('First Name'),
    lastName: Yup.string()
      .required()
      .min(1, 'Last Name Required')
      .label('Last Name'),
    email: Yup.string()
      .trim()
      .required('Please enter a registered email')
      .email()
      .label('Email'),
    offices: Yup.array().min(1, 'At least one office is required'),
    primaryOffice: Yup.object()
      .nullable()
      .required('Primary office is required'),
  });

const emptyFormValues: UserFormValues = {
  firstName: '',
  lastName: '',
  email: '',
  employeeId: undefined,
  supervisorId: undefined,
  disableDivisionAccess: false,
  restrictDivisionAccess: false,
  allowedJobIds: [],
  allowedCtcJobNumbers: [],
  roles: [],
  offices: [],
  divisions: [],
  primaryOffice: null,
};

const getInitialValues = (selectedUser?: UserDto): UserFormValues => {
  if (!selectedUser) return emptyFormValues;

  const offices =
    selectedUser.userOrganizationOffice
      ?.map((o) => o.organizationOffice!)
      .filter(Boolean) ?? [];

  const primaryOffice =
    offices.find((o) => o.id === selectedUser.primaryOfficeId) ?? null;

  return {
    id: selectedUser.id,
    firstName: selectedUser.firstName,
    lastName: selectedUser.lastName,
    email: selectedUser.email,
    employeeId: selectedUser.employeeId,
    supervisorId: selectedUser.supervisorId,
    disableDivisionAccess: selectedUser.disableDivisionAccess ?? false,
    restrictDivisionAccess: selectedUser.restrictDivisionAccess ?? false,
    allowedJobIds: selectedUser.allowedJobIds ?? [],
    allowedCtcJobNumbers: selectedUser.allowedCtcJobNumbers ?? [],
    roles:
      selectedUser.userOrganizationRole
        ?.map((r) => r.organizationRole!)
        .filter(Boolean) ?? [],
    offices,
    divisions:
      selectedUser.userDivision?.map((d) => d.division!).filter(Boolean) ?? [],
    primaryOffice,
  };
};

interface IUserFormProps {
  initialValues?: UserDto;
}

const normalizeEmail = (email: string | undefined): string =>
  (email ?? '').trim().toLowerCase();

export const UserForm: React.FC<IUserFormProps> = ({
  initialValues: selectedUser,
}) => {
  const navigate = useNavigate();
  const inviteUser = useInviteUser();
  const updateUser = useUpdateUser();
  const updateUserStatus = useUpdateUserStatus();
  const {
    user: currentUser,
    checkFeatureAllowed,
    assumeUser,
    isAdmin,
  } = useAuth();
  const isEditing = !!selectedUser?.id;
  const usersQuery = useAdminUsers(!isEditing);
  const archivedCheckPending = !isEditing && usersQuery.isPending;
  const archivedCheckFailed = !isEditing && usersQuery.isError;
  const isArchived = !!selectedUser?.isArchived;
  const [archivedMatch, setArchivedMatch] = React.useState<UserDto | null>(
    null,
  );

  const canSeeLoginHistory =
    selectedUser &&
    (checkFeatureAllowed(['settings_all', 'settings_login_history']) ||
      isAdmin);

  const [selectedSupervisor, setSelectedSupervisor] =
    React.useState<UserDto | null>(
      (selectedUser?.supervisor as UserDto) ?? null,
    );

  const updateStatus = async (status: string) => {
    if (!selectedUser) return;
    await updateUserStatus.mutateAsync({ id: selectedUser.id, status });
    showSuccessToast(
      `${selectedUser.firstName} ${selectedUser.lastName}'s account status updated!`,
    );
  };

  const handleSaveError = (err: unknown) => {
    const { applied, unmatchedMessages } = applyValidationErrors(
      err,
      Object.keys(formik.values),
      formik.setFieldError,
    );
    if (applied && unmatchedMessages.length === 0) {
      showErrorToast('Please fix the highlighted fields below.');
    } else {
      showErrorToast(getErrorMessage(err));
    }
  };

  const sendInvite = async (values: UserFormValues, restoring: boolean) => {
    const result = await inviteUser.mutateAsync(toInviteRequest(values));
    showSuccessToast(
      restoring ? `Restored ${result.fullName}` : `Added ${result.fullName}!`,
    );
    navigate(`/settings/users/edit/${result.id}`);
  };

  const formik = useFormik<UserFormValues>({
    initialValues: getInitialValues(selectedUser),
    validationSchema: createValidationSchema(),
    validateOnMount: true,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      try {
        if (!isEditing) {
          if (archivedCheckPending) return;
          const email = normalizeEmail(values.email);
          const archived = usersQuery.data?.find(
            (u) => u.isArchived && normalizeEmail(u.email) === email,
          );
          if (archived) {
            setArchivedMatch(archived);
            restore.requestOrRun();
            return;
          }
          await sendInvite(values, false);
        } else {
          await updateUser.mutateAsync({
            id: selectedUser.id,
            request: toUpdateRequest(values),
          });
          showSuccessToast(`${values.firstName} ${values.lastName} Updated!`);
        }
      } catch (err: unknown) {
        handleSaveError(err);
      } finally {
        setSubmitting(false);
      }
    },
  });

  // A failed restore is not rethrown: the mutation cache has already logged
  // it, and closing the modal uncovers any field errors applied to the form.
  const restore = useConfirmableAction(async () => {
    try {
      await sendInvite(formik.values, true);
    } catch (err: unknown) {
      handleSaveError(err);
    }
  }, true);

  const scopes = React.useMemo(() => {
    const items = formik.values.roles.flatMap(
      (r) => r.scopes ?? [],
    ) as string[];
    return uniq(items).sort();
  }, [formik.values.roles]);

  // Clear primary office when its office is removed from the Offices multi-select.
  // The primary office must always be one of the selected offices.
  React.useEffect(() => {
    const current = formik.values.primaryOffice;
    if (current && !formik.values.offices.some((o) => o.id === current.id)) {
      formik.setFieldValue('primaryOffice', null);
    }
  }, [formik.values.offices]);

  const officeDivisions = React.useMemo(() => {
    return formik.values.offices
      .flatMap((o) => o.divisions ?? [])
      .reduce((acc, curr) => {
        if (!acc.find((d) => d.id === curr.id)) {
          acc.push(curr);
        }
        return acc;
      }, [] as DivisionDto[])
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [formik.values.offices]);

  const handleSupervisorChange = React.useCallback(
    (user: UserDto | null) => {
      setSelectedSupervisor(user);
      formik.setFieldValue('supervisorId', user?.id ?? null);
    },
    [formik.setFieldValue],
  );

  const handleUserSelectDataFilter = React.useCallback(
    (users: UserDto[]) => {
      return users.filter((u) => u.id !== selectedUser?.id);
    },
    [selectedUser],
  );

  return (
    <Stack>
      <Group justify="flex-end" mb="md">
        {selectedUser && !isArchived && selectedUser.status !== 'disabled' && (
          <InlineConfirmButton
            color="red"
            confirmButtonProps={{
              children: 'Confirm Disable',
            }}
            onConfirmClick={async () => updateStatus('disabled')}
            loading={updateUserStatus.isPending}
          >
            Disable Account
          </InlineConfirmButton>
        )}
        {selectedUser && !isArchived && selectedUser.status === 'disabled' && (
          <InlineConfirmButton
            confirmButtonProps={{
              children: 'Confirm Enable',
            }}
            onConfirmClick={async () => updateStatus('confirmed')}
            loading={updateUserStatus.isPending}
          >
            Enable Account
          </InlineConfirmButton>
        )}
        {selectedUser &&
          (isArchived || selectedUser.id !== currentUser?.id) && (
            <ArchiveUserButton user={selectedUser} />
          )}
        <SecondaryButton onClick={() => navigate('/settings/users')}>
          Cancel
        </SecondaryButton>
      </Group>
      {archivedCheckFailed && (
        <Alert color="yellow" title="Archived-user check unavailable">
          The user list could not be loaded, so an archived user's email cannot
          be detected. Inviting an archived user's email will restore that user
          without asking for confirmation.
        </Alert>
      )}
      <Card>
        <Tabs defaultValue="profile" keepMounted={false}>
          <Tabs.List>
            <Tabs.Tab value="profile">Profile</Tabs.Tab>
            <Tabs.Tab value="permissions">Permissions</Tabs.Tab>
            <Tabs.Tab value="jobs">Jobs</Tabs.Tab>
            {canSeeLoginHistory && (
              <Tabs.Tab value="login-history">Login History</Tabs.Tab>
            )}
            {isAdmin && selectedUser && (
              <Tabs.Tab value="support">Support</Tabs.Tab>
            )}
          </Tabs.List>

          <Tabs.Panel value="profile" pt="md">
            <Grid maw={800}>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                  label="First Name"
                  placeholder="Enter first name..."
                  withAsterisk
                  value={formik.values.firstName}
                  onChange={(e) =>
                    formik.setFieldValue('firstName', e.target.value)
                  }
                  onBlur={() => formik.setFieldTouched('firstName')}
                  error={formik.touched.firstName && formik.errors.firstName}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                  label="Last Name"
                  placeholder="Enter last name..."
                  withAsterisk
                  value={formik.values.lastName}
                  onChange={(e) =>
                    formik.setFieldValue('lastName', e.target.value)
                  }
                  onBlur={() => formik.setFieldTouched('lastName')}
                  error={formik.touched.lastName && formik.errors.lastName}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                  label="Email Address"
                  placeholder="Enter email..."
                  value={formik.values.email}
                  withAsterisk
                  onChange={(e) =>
                    formik.setFieldValue('email', e.target.value)
                  }
                  onBlur={() => formik.setFieldTouched('email')}
                  error={formik.touched.email && formik.errors.email}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                  label="Employee ID"
                  placeholder="Enter employee ID..."
                  value={formik.values.employeeId}
                  onChange={(e) =>
                    formik.setFieldValue('employeeId', e.target.value)
                  }
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Input.Wrapper label="Supervisor">
                  <UserSelect
                    hideLabel
                    value={selectedSupervisor}
                    onChange={handleSupervisorChange}
                    dataFilter={handleUserSelectDataFilter}
                  />
                </Input.Wrapper>
              </Grid.Col>
              <Grid.Col span={12}>
                <Stack gap="xs">
                  <Text c="black">
                    <b>Classification:</b>{' '}
                    {selectedUser?.classificationName || ''}
                  </Text>
                  <Text c="black">
                    <b>Level:</b> {selectedUser?.classificationLevelName || ''}
                  </Text>
                </Stack>
              </Grid.Col>
              {selectedUser && (
                <Grid.Col span={12}>
                  {/* Read-only — admins can view but not set another user's phone or consent. */}
                  <Stack gap="xs">
                    <Text fw={600} size="sm">
                      Notifications
                    </Text>
                    <Text c="black" size="sm">
                      <b>Phone:</b> {selectedUser.phoneNumber || 'Not on file'}
                    </Text>
                    <Text c="black" size="sm">
                      <b>Phone Verified:</b>{' '}
                      {selectedUser.phoneNumberVerifiedAt
                        ? formatDate(selectedUser.phoneNumberVerifiedAt)
                        : 'Not verified'}
                    </Text>
                    <Text c="black" size="sm">
                      <b>SMS Consent Status:</b> {selectedUser.smsConsentStatus}
                    </Text>
                    <Text c="black" size="sm">
                      <b>Notification Channels:</b>
                    </Text>
                    <NotificationChannelStatus user={selectedUser} />
                  </Stack>
                </Grid.Col>
              )}
            </Grid>

            {selectedUser && isArchived && (
              <Alert color="gray" title="Archived User" mt="md">
                {`This user is archived${
                  selectedUser.archivedAt
                    ? ` (on ${formatDate(selectedUser.archivedAt)})`
                    : ''
                }. They are hidden from pickers and lists and cannot sign in.`}
              </Alert>
            )}
            {selectedUser && !isArchived && selectedUser.status === 'disabled' && (
              <Alert color="yellow" title="Disabled Account" mt="md">
                This account is disabled. The user is not able to login.
              </Alert>
            )}
          </Tabs.Panel>
          <Tabs.Panel value="permissions" pt="md">
            <Stack>
              <Input.Wrapper
                label="Offices"
                description="Offices determine what divisions are available for selection."
                withAsterisk
              >
                <OfficeSelector
                  onChange={(value) => formik.setFieldValue('offices', value)}
                  initialValue={formik.values.offices}
                />
              </Input.Wrapper>

              <Input.Wrapper
                label="Primary Office"
                description="The office responsible for administering this user's time data. Only this office's admins will see this user in timesheet reports."
                withAsterisk
              >
                <Select
                  placeholder={
                    formik.values.offices.length === 0
                      ? 'Select at least one office above...'
                      : 'Select primary office...'
                  }
                  data={formik.values.offices.map((o) => ({
                    value: String(o.id),
                    label: o.name,
                  }))}
                  value={
                    formik.values.primaryOffice
                      ? String(formik.values.primaryOffice.id)
                      : null
                  }
                  onChange={(value) => {
                    const selected =
                      formik.values.offices.find(
                        (o) => String(o.id) === value,
                      ) ?? null;
                    formik.setFieldValue('primaryOffice', selected);
                  }}
                  disabled={formik.values.offices.length === 0}
                  error={
                    formik.touched.primaryOffice &&
                    (formik.errors.primaryOffice as string)
                  }
                  clearable
                  searchable
                />
              </Input.Wrapper>

              <Checkbox
                label="Ignore Division Access"
                description="If checked, the user must be assigned a job to view it. Their division access will be ignored."
                checked={formik.values.disableDivisionAccess}
                onChange={(value: any) =>
                  formik.setFieldValue(
                    'disableDivisionAccess',
                    value.target.checked,
                  )
                }
              />

              <Checkbox
                label="Restrict Division Access"
                description="If checked, the user must be assigned a division to view it. Their office access will be ignored."
                checked={formik.values.restrictDivisionAccess}
                disabled={formik.values.disableDivisionAccess}
                onChange={(value: any) =>
                  formik.setFieldValue(
                    'restrictDivisionAccess',
                    value.target.checked,
                  )
                }
              />

              <Input.Wrapper
                label="Divisions"
                description="Divisions control view access to CTC. If the user has a division, they may view any job in that division."
              >
                {formik.values.restrictDivisionAccess ? (
                  <OfficeDivisionSelector
                    onChange={(value) =>
                      formik.setFieldValue('divisions', value)
                    }
                    offices={formik.values.offices}
                    value={formik.values.divisions}
                  />
                ) : (
                  <DivisionList divisions={officeDivisions} maxItems={6} />
                )}
              </Input.Wrapper>

              <Input.Wrapper label="Roles">
                <RoleSelector2
                  value={formik.values.roles}
                  onChange={(value) => formik.setFieldValue('roles', value)}
                  initialValue={formik.values.roles}
                />
              </Input.Wrapper>
              {scopes.length > 0 && (
                <Input.Wrapper
                  label="Scopes"
                  description="Scopes determine what the user is able view, read, write, and manage. They can only be derived from roles."
                >
                  <TagBadgeList tags={scopes} maxItems={6} />
                </Input.Wrapper>
              )}
            </Stack>
          </Tabs.Panel>
          <Tabs.Panel value="jobs" pt="md">
            <Stack>
              <Group>
                <Text>
                  Allowed Jobs override any permissions assigned to an office.
                  If no jobs are selected, office permissions will be used.
                </Text>
                <Text
                  c={
                    formik.values.allowedJobIds.length +
                      formik.values.allowedCtcJobNumbers.length >
                    0
                      ? 'blue'
                      : 'dimmed'
                  }
                >
                  {formik.values.allowedJobIds.length +
                    formik.values.allowedCtcJobNumbers.length}{' '}
                  Allowed Jobs
                </Text>
              </Group>

              <JobGrantSelector
                initialJobIds={formik.values.allowedJobIds}
                initialCtcJobNumbers={formik.values.allowedCtcJobNumbers}
                onChange={(jobIds, ctcNums) => {
                  formik.setFieldValue('allowedJobIds', jobIds);
                  formik.setFieldValue('allowedCtcJobNumbers', ctcNums);
                }}
              />
            </Stack>
          </Tabs.Panel>
          {canSeeLoginHistory && (
            <Tabs.Panel value="login-history" pt="md">
              <UserLogins
                userId={selectedUser?.id}
                userName={`${selectedUser?.firstName}_${selectedUser?.lastName}`}
              />
            </Tabs.Panel>
          )}
          {isAdmin && selectedUser && (
            <Tabs.Panel value="support" pt="md">
              <Stack>
                <Box maw={600}>
                  <Text size="sm" c="dimmed">
                    Assume this user's identity to debug their permissions and
                    visibility. Your session will appear as this user until you
                    stop assuming, close your browser, or refresh the page.
                  </Text>
                  <Text size="sm" c="dimmed">
                    If you made changes to this user, save your changes first!
                  </Text>
                  {scopes.includes('sys_admin') && (
                    <Text size="sm" c="red" mt="sm">
                      Cannot assume a sys_admin user.
                    </Text>
                  )}
                </Box>
                <ModalButton
                  leftSection={<IconUserSearch size={16} />}
                  disabled={scopes.includes('sys_admin')}
                  w="fit-content"
                  onClick={async () => {
                    await assumeUser(selectedUser.id);
                    navigate('/');
                  }}
                >
                  Assume User
                </ModalButton>
              </Stack>
            </Tabs.Panel>
          )}
        </Tabs>
      </Card>

      <Group mt="md">
        {isEditing ? (
          <SaveButton
            onClick={() => formik.submitForm()}
            loading={formik.isSubmitting}
            disabled={!formik.isValid || !formik.dirty}
          />
        ) : (
          <CreateButton
            onClick={() => formik.submitForm()}
            loading={formik.isSubmitting || archivedCheckPending}
            disabled={!formik.isValid || !formik.dirty}
          >
            Create User
          </CreateButton>
        )}
      </Group>
      <RestoreArchivedUserModal
        archivedUser={archivedMatch}
        opened={restore.opened}
        loading={restore.loading}
        onConfirm={() => restore.confirm()}
        onClose={restore.close}
      />
    </Stack>
  );
};
